import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Compass,
  Handshake,
  LayoutDashboard,
  MessageCircle,
  MessageSquare,
  User,
  X,
} from 'lucide-react';
import { clearPostLoginFeaturePromptPending, isPostLoginFeaturePromptPending } from '../services/auth';
import { getEnabledMenuDefinitions } from '../config/navigation';
import { useLocalization } from '../context/LocalizationContext';

const iconMap = {
  dashboard: LayoutDashboard,
  surety: Handshake,
  services: MessageSquare,
  feedback: MessageCircle,
  profile: User,
};

export const PostLoginFeaturePrompt = ({ userName = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { needsCompactFont, t } = useLocalization();
  const [isOpen, setIsOpen] = React.useState(false);

  const enabledFeatures = React.useMemo(() => getEnabledMenuDefinitions(), []);
  const primaryFeature =
    enabledFeatures.find((item) => item.id === 'dashboard') || enabledFeatures[0] || null;

  React.useEffect(() => {
    if (isPostLoginFeaturePromptPending() && enabledFeatures.length > 0) {
      setIsOpen(true);
    }
  }, [enabledFeatures.length]);

  const closePrompt = React.useCallback(() => {
    clearPostLoginFeaturePromptPending();
    setIsOpen(false);
  }, []);

  const openFeature = React.useCallback(
    (path) => {
      clearPostLoginFeaturePromptPending();
      setIsOpen(false);
      if (path && location.pathname !== path) {
        navigate(path);
      }
    },
    [location.pathname, navigate]
  );

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closePrompt();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closePrompt, isOpen]);

  if (!isOpen || enabledFeatures.length === 0) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto overscroll-contain px-3 py-3 sm:flex sm:items-center sm:justify-center sm:px-4 sm:py-6">
      <button
        type="button"
        className="absolute inset-0 bg-slate-950/55 backdrop-blur-sm"
        onClick={closePrompt}
        aria-label={t('onboarding.close')}
      />

      <div className="app-modal-surface relative z-10 my-auto w-full max-w-3xl overflow-hidden rounded-[1.75rem] max-sm:min-h-[calc(100dvh-1.5rem)] max-sm:rounded-[1.5rem] sm:max-h-[calc(100vh-3rem)]">
        <div className="max-h-[calc(100dvh-1.5rem)] overflow-y-auto px-4 pb-4 pt-4 sm:max-h-[calc(100vh-3rem)] sm:px-6 sm:pb-6 sm:pt-6">
          <div className="sticky top-0 z-10 -mx-4 -mt-4 mb-4 flex items-start justify-between gap-3 border-b border-slate-200/80 bg-white/95 px-4 py-3 backdrop-blur-sm dark:border-slate-800 dark:bg-slate-900/95 sm:static sm:m-0 sm:border-b-0 sm:bg-transparent sm:p-0">
            <div className="flex min-w-0 items-start gap-4 pr-2 sm:pr-10">
              <div className="app-icon-chip h-12 w-12 flex-shrink-0 bg-primary-500 text-white shadow-md">
                <Compass size={22} strokeWidth={1.9} />
              </div>
              <div className="min-w-0">
                <p
                  className={`text-xs font-semibold text-primary-600 dark:text-primary-300 ${
                    needsCompactFont ? 'tracking-normal' : 'uppercase tracking-[0.24em]'
                  }`}
                >
                  {t('onboarding.kicker')}
                </p>
                <h2 className={`app-panel-title mt-1 ${needsCompactFont ? 'app-panel-title-te' : ''}`}>
                  {t('onboarding.title')}
                </h2>
                <p className="mt-2 text-sm leading-6 text-primary-700 dark:text-primary-200">
                  {t('onboarding.subtitle', { name: userName || t('common.member') })}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={closePrompt}
              className="rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
              aria-label={t('onboarding.close')}
            >
              <X size={18} />
            </button>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {enabledFeatures.map((item) => {
              const Icon = iconMap[item.id] || Compass;
              const isCurrent = location.pathname === item.path;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => openFeature(item.path)}
                  className="group app-surface-muted p-4 text-left transition-all hover:border-primary-300 hover:bg-white hover:shadow-md dark:hover:border-primary-500/40 dark:hover:bg-slate-800"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 gap-3">
                      <div className="app-icon-chip mt-0.5 h-10 w-10 flex-shrink-0 rounded-xl">
                        <Icon size={18} strokeWidth={1.8} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-base font-semibold text-primary-900 dark:text-white">{t(item.labelKey)}</p>
                        <p className="mt-1 text-sm leading-5 text-primary-700 dark:text-primary-200">
                          {t(item.descriptionKey)}
                        </p>
                      </div>
                    </div>
                    <ArrowRight
                      size={18}
                      className="mt-1 flex-shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:text-primary-500"
                    />
                  </div>
                  <p className="mt-3 text-xs font-medium uppercase tracking-wide text-primary-600 dark:text-primary-300">
                    {isCurrent ? t('onboarding.continueHere') : t('onboarding.openFeature', { feature: t(item.labelKey) })}
                  </p>
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-primary-600 dark:text-primary-300">{t('onboarding.footerNote')}</p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={closePrompt}
                className="app-secondary-button"
              >
                {t('onboarding.later')}
              </button>
              {primaryFeature && (
                <button
                  type="button"
                  onClick={() => openFeature(primaryFeature.path)}
                  className="app-primary-button"
                >
                  {t('onboarding.startWith', { feature: t(primaryFeature.labelKey) })}
                  <ArrowRight size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
